import { useMutation } from '@tanstack/react-query';
import { refreshBulkOp } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import { BulkImportModel } from '../../../../types';
import { Messages } from '../../../utils/message-constants';
import SharedDialog from '../../../shared/shared-dialog';
import { useBulkOpActions } from './hooks/use-bulk-op-actions';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface RefreshBulkOpsDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedBulkOp: BulkImportModel;
}

export default function RefreshBulkOpsDialog({
  open,
  handleClose,
  selectedBulkOp,
}: RefreshBulkOpsDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const { patchLastExportUpdate } = useBulkOpActions();

  const { mutate, isPending } = useMutation({
    mutationFn: () => refreshBulkOp(selectedBulkOp),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: Messages.Success.Started('Spreadsheet data refresh'),
      });

      patchLastExportUpdate(selectedBulkOp.id);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'this bulk operation spreadsheet'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.import_export.bulk_operations
          .refresh_bulk_operation_spreadsheet,
        {
          module: ModuleList.modules.automation.name,
          section: ModuleList.modules.automation.sections.bulkOperations,
        }
      );
    },
  });

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Refresh Spreadsheet Data"
      dialogContent={
        <Stack gap={2}>
          <Stack>
            <Typography>
              This will refresh the data on the spreadsheet.
            </Typography>
            <Typography>
              This will clear out any changes you have made to the spreadsheet
              and populate it with the latest data.
            </Typography>
          </Stack>
          <Typography>
            If you have any filters on this sheet, please remove them. They will
            prevent the sheet from being refreshed.
          </Typography>
          <Typography>Do you wish to continue?</Typography>
        </Stack>
      }
      primaryButton={{
        title: 'Refresh',
        action: () => mutate(),
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
